import BluebirdPromise from 'bluebird';
import cns from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import { PureComponent } from 'react';
import { RuntimeContext } from '../../../../../common/RuntimeContext';
import { getLocalStorage, setLocalStorage } from '../../../../shared/actions/LocalStorage';
import LoadingIndicator from '../../../../shared/components/LoadingIndicator';
import ScheduleModal from '../../../../shared/components/scheduleModal/ScheduleModal';
import { SEGMENT_EVENTS } from '../../../../shared/Constants';
import BrowsePathways from './BrowsePathways';
import MyPathway from './MyPathway';
import PathwayDetails from './PathwayDetails';

export default class PathwayPanelWidget extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            page: 1,
            pathways: [],
            pathwayView: 'loading',
            pathwayViewLoad: true,
            limit: 5,
            currentPathway: {},
            currentUserPathway: {},
            animationClass: 'invisible',
            calendarModal: false,
            calendarEventInfo: null,
            teamUserPathways: [],
            individualUserPathways: [],
            showCompletedPathways: false,
            completedPathways: [],
            completedPathwaysToDisplay: [],
            numPathwayToShow: this._calculateMaxNumOfPathwayToShow({ _id: true })
        };
    }

    static propTypes = {
        getManyPathways: PropTypes.func,
        getManyUserPathways: PropTypes.func,
        getPathway: PropTypes.func,
        userProfile: PropTypes.object,
        currentWidth: PropTypes.number,
        collectionsById: PropTypes.object,
        postUserPathway: PropTypes.func,
        putUserPathway: PropTypes.func,
        userCollectionsById: PropTypes.object,
        userCompany: PropTypes.object,
        userPathways: PropTypes.array,
        isVerbAdmin: PropTypes.bool,
        setWiderPanel: PropTypes.func,
        getManyUserCollections: PropTypes.func,
        history: PropTypes.object,
        setPanelLoadingOverlay: PropTypes.func,
        teamsById: PropTypes.object,
        pathwayView: PropTypes.string,
        pathwayPermalink: PropTypes.string
    };

    static contextType = RuntimeContext

    // eslint-disable-next-line class-methods-use-this
    _calculateMaxNumOfPathwayToShow(userPathway = {}) {
        const hasCurrentUserPath = !!userPathway._id;
        const numOfPathwaysToShow = hasCurrentUserPath
            ? Math.floor((window.outerHeight - 396) / 120)
            : Math.floor((window.outerHeight - 481) / 120);
        return numOfPathwaysToShow;
    }

    componentDidMount() {
        const {
            getManyUserPathways,
            getManyPathways,
            userProfile,
            userCompany,
            history,
            pathwayView = '',
            pathwayPermalink
        } = this.props;
        const { limit, numPathwayToShow } = this.state;

        const getOptions = { page: 1, limit: numPathwayToShow, sortField: 'name', sortOrder: 1 };

        // retrieve UserPathways first so we can filter out pathway request for pathways already in progress
        return getManyUserPathways({ pathsContext: this.context.pathsWeb, userProfileId: userProfile._id }).then((userPathways) => {
            getOptions._id = (userCompany.pathwayOptions || []).map((pathway) => pathway.id);
            getOptions.getAllAvailablePathways = true;

            // exclude user pathways
            getOptions.excludePathways = [...(userPathways || []).map((pathway) => pathway.pathwayId)];
            getOptions.pathsContext = this.context.pathsWeb;

            const promises = [getManyPathways(getOptions)];
            if (
                (!userCompany.pathwayOptions || !userCompany.pathwayOptions.length) &&
                userCompany.pathwayLicenses &&
                userCompany.pathwayLicenses.length
            ) {
                const getCompanyExclusiveOptions = { page: 1, limit, sortField: 'name', sortOrder: 1 };
                getCompanyExclusiveOptions._id = userCompany.pathwayLicenses.map((path) => path.pathwayId);
                getCompanyExclusiveOptions.pathsContext = this.context.pathsWeb;
                promises.push(getManyPathways(getCompanyExclusiveOptions));
            }

            return BluebirdPromise.all(promises).spread((pathways, companyExclusivePathways) => {
                const showPathwayList = pathways;
                if (companyExclusivePathways && companyExclusivePathways.length) {
                    companyExclusivePathways.forEach((pathway) => {
                        if (showPathwayList.findIndex((path) => path._id === pathway._id) === -1) {
                            showPathwayList.push(pathway);
                        }
                    });
                }

                let {
                    currentUserPathway,
                    filteredPathways,
                    teamUserPathways,
                    completedPathways,
                    individualUserPathways
                } = this._filterPathwaysForUserPathways(showPathwayList, userPathways, pathwayPermalink);

                let pathwayViewName = !currentUserPathway._id ? 'browse' : 'myPathway';
                const { pathwayId = '', userPathwayId = '' } = querystring.parse(window.location.search);
                let customSearchParams;

                let currentPathway = {};
                if (pathwayId) {
                    pathwayViewName = 'detail';
                    currentPathway = filteredPathways.find((path) => path._id === pathwayId);
                }

                // if pathwayPermalink and there is no userPathway, set to details page for pathway
                if (
                    pathwayPermalink &&
                    (isEmpty(currentUserPathway) || !currentUserPathway.pathwayId !== pathwayPermalink)
                ) {
                    // if pathway is not available to the company don't set to details page
                    const permalinkPathway = filteredPathways.find((path) => path._id === pathwayPermalink);
                    if (permalinkPathway && !isEmpty(permalinkPathway)) {
                        pathwayViewName = 'detail';
                        customSearchParams = `&pathwayId=${pathwayPermalink}`;
                        currentPathway = permalinkPathway;
                    }
                }

                if (userPathwayId) {
                    pathwayViewName = 'myPathway';
                    currentUserPathway = userPathways.find((path) => path._id === userPathwayId);
                }

                this.setState(
                    {
                        pathways: filteredPathways,
                        allPathways: showPathwayList,
                        currentUserPathway,
                        page: 2,
                        pathwayViewLoad: false,
                        currentPathway,
                        hideLoadMore: pathways.length < numPathwayToShow,
                        animationClass: 'fadein',
                        teamUserPathways,
                        completedPathways,
                        limit: numPathwayToShow,
                        individualUserPathways
                    },
                    () => {
                        // push pathway panel view to url if from /home without pathwayView search params
                        if (!pathwayView || pathwayViewName !== pathwayView) {
                            const historySearch = this.removeParamsFromSearchParams([
                                'pathwayPermalink',
                                'pathwayView'
                            ]);
                            const searchParams = historySearch
                                ? `${historySearch}&pathwayView=${pathwayViewName}${customSearchParams || ''}`
                                : `?pathwayView=${pathwayViewName}${customSearchParams || ''}`;
                            history.push(`/home${searchParams}`);
                        }
                    }
                );
            });
        });
    }

    setCurrentUserPathway = (currentUserPathway) => this.setState({ currentUserPathway });

    getUserTeamsNewestFirst = () => {
        const {
            userProfile: { teams = [] }
        } = this.props;
        const userTeams = teams.sort((a, b) => new Date(b.joinedDateTime) - new Date(a.joinedDateTime));
        return userTeams;
    };

    removeParamsFromSearchParams = (paramNameArray) => {
        const { history } = this.props;
        let searchParamsArray = history.location.search.slice(1).split('&');
        searchParamsArray = searchParamsArray.filter((param) =>
            paramNameArray.every((paramName) => !param.includes(paramName))
        );
        return searchParamsArray.length ? `?${searchParamsArray.join('&')}` : null;
    };

    _handleShowCompletedPathways = (showCompleted = false, currentlySelectedPathway = {}, completedPathwaysArray) => {
        this.setState(({ completedPathways }) => {
            let pathways = completedPathwaysArray || completedPathways;
            return {
                showCompletedPathways: showCompleted,
                completedPathwaysToDisplay: showCompleted
                    ? [...pathways].sort(
                          (a, b) =>
                              (a.name.toLowerCase() > b.name.toLowerCase()) -
                              (a.name.toLowerCase() < b.name.toLowerCase())
                      )
                    : [{ ...currentlySelectedPathway }].filter((pathway) => !isEmpty(pathway))
            };
        });
    };

    _filterPathwaysForUserPathways = (pathways, userPathways, pathwayPermalink) => {
        let currentUserPath = {};
        const teamUserPathways = [];
        const completedPathways = [];
        const individualUserPathways = [];
        const userPathwaysByTeamAndPathwayId = {};
        let newFilteredPathways = [...pathways];
        userPathways.forEach((userPathway) => {
            // removes completed pathway so that pathway will not show in choose a new pathway
            if (userPathway.isCompleted) {
                const removeIdx = newFilteredPathways.findIndex((path) => path._id === userPathway.pathwayId);
                if (removeIdx > -1) {
                    newFilteredPathways.splice(removeIdx, 1);
                }

                // set currentUserPath to completedPathway if it's equal to pathwayPermalink
                if (userPathway.pathwayId === pathwayPermalink) {
                    currentUserPath = userPathway;
                }
                completedPathways.push(userPathway);
            }

            // in progress individual pathways
            if (!userPathway.isCompleted && !userPathway.isDeleted && !userPathway.teamId) {
                if (userPathway.isCurrent && isEmpty(currentUserPath)) {
                    currentUserPath = userPathway;
                }

                // set currentUserPath to completedPathway if it's equal to pathwayPermalink
                if (userPathway.pathwayId === pathwayPermalink) {
                    currentUserPath = userPathway;
                }
                const removeIdx = newFilteredPathways.findIndex((path) => path._id === userPathway.pathwayId);
                if (removeIdx > -1) {
                    newFilteredPathways.splice(removeIdx, 1);
                }
                individualUserPathways.push(userPathway);
            }

            // team pathways
            if (userPathway.teamId && !userPathway.isCompleted) {
                userPathwaysByTeamAndPathwayId[`${userPathway.teamId}-${userPathway.pathwayId}`] = userPathway;
                if (isEmpty(currentUserPath)) {
                    currentUserPath = userPathway;
                }
                teamUserPathways.push(userPathway);
                const removeIdx = newFilteredPathways.findIndex((path) => path._id === userPathway.pathwayId);
                if (removeIdx > -1) {
                    newFilteredPathways.splice(removeIdx, 1);
                }
            }
        });

        if (pathwayPermalink && (isEmpty(currentUserPath) || currentUserPath.pathwayId !== pathwayPermalink)) {
            const userTeams = this.getUserTeamsNewestFirst();
            userTeams.forEach((team) => {
                if (
                    (isEmpty(currentUserPath) || currentUserPath.pathwayId !== pathwayPermalink) &&
                    currentUserPath.pathwayId !== pathwayPermalink
                ) {
                    if (userPathwaysByTeamAndPathwayId[`${team.teamId}-${pathwayPermalink}`]) {
                        currentUserPath = userPathwaysByTeamAndPathwayId[`${team.teamId}-${pathwayPermalink}`];
                    }
                }
            });
        }

        newFilteredPathways = newFilteredPathways.sort((a, b) => (a.name > b.name) - (a.name < b.name));
        if (isEmpty(currentUserPath) && individualUserPathways.length > 1) {
            [currentUserPath] = individualUserPathways
                .filter((path) => !path.isCurrent)
                .sort((a, b) => a.name - b.name);
        }
        if (!currentUserPath?._id && teamUserPathways[0]) {
            [currentUserPath] = teamUserPathways.sort((a, b) => a.updatedOn - b.updatedOn);
        }

        const localCurrentUserPathwayId = getLocalStorage({ key: 'selectedPathway', dispatchDisabled: true });
        let localCurrentUserPathway = {};

        if (localCurrentUserPathwayId && !pathwayPermalink) {
            [localCurrentUserPathway] = [...individualUserPathways, ...teamUserPathways].filter(
                ({ _id }) => _id === localCurrentUserPathwayId
            );
        }
        if (pathwayPermalink && !isEmpty(currentUserPath) && currentUserPath.pathwayId === pathwayPermalink) {
            setLocalStorage({ key: 'selectedPathway', value: currentUserPath._id, dispatchDisabled: true });
            if (currentUserPath.isCompleted) {
                this._handleShowCompletedPathways(true, {}, completedPathways);
            }
        }

        const currentSelectedUserPathway = isEmpty(localCurrentUserPathway) ? currentUserPath : localCurrentUserPathway;

        return {
            currentUserPathway: currentSelectedUserPathway,
            filteredPathways: newFilteredPathways,
            teamUserPathways,
            completedPathways,
            individualUserPathways
        };
    };

    fetchPathways = (limit) => {
        const { page } = this.state;
        const { getManyPathways, userPathways, userCompany, isVerbAdmin } = this.props;

        let ids = [];

        ids = (userCompany.pathwayOptions || []).map((pathway) => pathway.id);

        const options = {
            getAllAvailablePathways: true,
            excludePathways: [...(userPathways || []).map((pathway) => pathway.pathwayId)],
            page,
            limit,
            sortField: 'name',
            sortOrder: 1,
            pathsContext: this.context.pathsWeb

        };
        if (ids.length) {
            options._id = ids;
        }

        return getManyPathways(options).then((newFetchedPathways) => {
            this.setState((prevstate) => {
                const { pathways, page, allPathways } = prevstate;
                const newPathwaysList = pathways;
                if (newFetchedPathways && newFetchedPathways.length) {
                    newFetchedPathways.forEach((pathway) => {
                        if (newPathwaysList.findIndex((path) => path._id === pathway._id) === -1) {
                            newPathwaysList.push(pathway);
                        }
                    });
                }
                const newAllPathways = allPathways.concat(newFetchedPathways);
                const {
                    currentUserPathway,
                    filteredPathways,
                    completedPathways,
                    teamUserPathways,
                    individualUserPathways
                } = this._filterPathwaysForUserPathways(newPathwaysList, userPathways);

                return {
                    pathways: filteredPathways,
                    page: page + 1,
                    currentUserPathway,
                    allPathways: newAllPathways,
                    hideLoadMore: newFetchedPathways.length < limit,
                    completedPathways,
                    teamUserPathways,
                    individualUserPathways
                };
            });
            return newFetchedPathways;
        });
    };

    openPathwayDetails = (pathwayId) => {
        const { getPathway, setWiderPanel, history } = this.props;
        this.setState({ animationClass: 'fadeout' });
        return getPathway({ pathwayId, pathsContext: this.context.pathsWeb  }).then((pathway) => {
            setTimeout(() => {
                this.setState(
                    {
                        currentPathway: pathway[0],
                        animationClass: 'fadein'
                    },
                    () => {
                        setWiderPanel(true);
                    }
                );
                return history.push(`/home?pathwayView=detail&pathwayId=${pathwayId}`);
            }, 500);
        });
    };

    onClickBack = (view) => {
        const { userCompany, isVerbAdmin, setWiderPanel, history } = this.props;
        const { currentPathway, pathwayView } = this.state;
        this.setState({ animationClass: 'fadeout' });
        setTimeout(() => {
            this.setState((prevstate) => {
                const { currentPathway } = prevstate;
                setWiderPanel(view === 'detail');
                return {
                    pathwayView: view,
                    currentPathway: view === 'browse' ? {} : currentPathway,
                    animationClass: 'fadein'
                };
            });
        }, 500);
        return history.push(`/home?pathwayView=${view}`);
    };

    toggleCalendarModal = (eventInfo) => {
        this.setState((prevstate) => {
            const { calendarModal } = prevstate;
            const returnState = {
                calendarModal: !calendarModal
            };
            if (!prevstate.calendarModal && eventInfo) {
                returnState.calendarEventInfo = eventInfo;
            } else if (prevstate.calendarModal) {
                returnState.calendarEventInfo = null;
            }
            return returnState;
        });
    };

    updateUserPathway = (userPathway) => {
        const { putUserPathway, setPanelLoadingOverlay } = this.props;
        const { currentUserPathway } = this.state;

        this.setState({
            panelLoadingOverlay: true
        });

        setPanelLoadingOverlay(true);

        const putBody = {
            userPathwayId: currentUserPathway._id,
            body: {
                pathwayId: currentUserPathway.pathwayId,
                name: currentUserPathway.name,
                content: userPathway.content,
                isCurrent: true,
                revision: currentUserPathway.revision,
                isCompleted: currentUserPathway.isCompleted
            },
            pathsContext: this.context.pathsWeb
        };
        return putUserPathway(putBody).then((userPathway) => {
            const { userPathways } = this.props;
            this.setState((prevstate) => {
                const { allPathways } = prevstate;
                const { filteredPathways } = this._filterPathwaysForUserPathways(allPathways, userPathways);
                return {
                    currentUserPathway: userPathway,
                    pathways: filteredPathways,
                    pathwayView: 'myPathway',
                    modalState: false,
                    animationClass: 'fadein',
                    panelLoadingOverlay: false
                };
            });
            setPanelLoadingOverlay(false);
        });
    };

    selectPathway = (id, name, isChanging = false) => {
        const { postUserPathway, putUserPathway, userPathways, userCompany, isVerbAdmin, getManyUserCollections } =
            this.props;
        const { currentUserPathway, currentPathway } = this.state;
        this.setState({ animationClass: 'fadeout' });
        let postBody = { body: { pathwayId: id, name, isCurrent: true } };

        const pathwayIdx = userPathways.findIndex((userPath) => userPath.pathwayId === id);

        if (userPathways[pathwayIdx]?._id) {
            setLocalStorage({ key: 'selectedPathway', value: userPathways[pathwayIdx]?._id, dispatchDisabled: true });
        }

        const completedCurrentPathIdx = userPathways.findIndex(
            (userPath) => userPath.isCurrent && userPath.isCompleted
        );

        let postFunc = postUserPathway;
        let newSelectedUserPathway = currentPathway;

        // update userPathway if user has previously started pathway.
        if (pathwayIdx > -1) {
            postFunc = putUserPathway;
            newSelectedUserPathway = userPathways[pathwayIdx];

            postBody = {
                userPathwayId: newSelectedUserPathway._id,
                body: {
                    pathwayId: newSelectedUserPathway.pathwayId,
                    name: newSelectedUserPathway.name,
                    isCurrent: true,
                    revision: newSelectedUserPathway.revision
                },
                pathsContext: this.context.pathsWeb
            };
        }

        const promises = [postFunc(postBody)];

        // update currentUserPathway to set new pathway as current
        if (isChanging) {
            const previousUserPathUpdateBody = {
                userPathwayId: currentUserPathway._id,
                body: {
                    pathwayId: currentUserPathway.pathwayId,
                    name: currentUserPathway.name,
                    isCurrent: false,
                    revision: currentUserPathway.revision
                },
                pathsContext: this.context.pathsWeb
            };
            promises.push(putUserPathway(previousUserPathUpdateBody));
        }

        if (completedCurrentPathIdx > -1) {
            const previousCurrentPathway = userPathways[completedCurrentPathIdx];

            const previousUserPathUpdateBody = {
                userPathwayId: previousCurrentPathway._id,
                body: {
                    pathwayId: previousCurrentPathway.pathwayId,
                    name: previousCurrentPathway.name,
                    isCurrent: false,
                    revision: previousCurrentPathway.revision
                },
                pathsContext: this.context.pathsWeb
            };
            promises.push(putUserPathway(previousUserPathUpdateBody));
        }

        return BluebirdPromise.all(promises).spread((newCurrentUserPathway) => {
            const { userPathways, setWiderPanel, history } = this.props;
            const ucIds = newCurrentUserPathway.content
                ? newCurrentUserPathway.content
                      .filter((item) => !!item.userSubscriptionId)
                      .map((item) => item.userSubscriptionId)
                : [];
            this.setState((prevstate) => {
                const { allPathways } = prevstate;
                const { filteredPathways, individualUserPathways } = this._filterPathwaysForUserPathways(
                    allPathways,
                    userPathways
                );
                setLocalStorage({ key: 'selectedPathway', newCurrentUserPathway, dispatchDisabled: true });
                setWiderPanel(false);
                return {
                    currentUserPathway: newCurrentUserPathway,
                    pathways: filteredPathways,
                    pathwayView: 'myPathway',
                    animationClass: 'fadein',
                    individualUserPathways
                };
            });
            history.push(`/home?pathwayView=myPathway`);
            return getManyUserCollections({ _id: ucIds, pathsContext: this.context.pathsWeb });
        });
    };

    updateUserPathwayContentItem = (milestone, idx = null) => {
        const { putUserPathway } = this.props;
        const { currentUserPathway } = this.state;
        const milestoneIdx = milestone.pathwayContentIndex?.toString() || idx?.toString();

        if (milestoneIdx) {
            const newStatus =
                currentUserPathway.content[milestoneIdx].status === 'COMPLETED' ? 'ASSIGNED' : 'COMPLETED';
            currentUserPathway.content[milestoneIdx].status = newStatus;
            currentUserPathway.content[milestoneIdx].isCompleted =
                !currentUserPathway.content[milestoneIdx].isCompleted;
        }
        const putBody = {
            userPathwayId: currentUserPathway._id,
            body: {
                pathwayId: currentUserPathway.pathwayId,
                name: currentUserPathway.name,
                content: currentUserPathway.content,
                revision: currentUserPathway.revision
            },
            pathsContext: this.context.pathsWeb
        };
        const segmentInfo = {
            dateTime: milestone.date,
            taskName: milestone.name,
            pathwayName: currentUserPathway.name,
            licenseType: currentUserPathway.subscriptionType
        };
        return putUserPathway(putBody).then((updatedPathway) => {
            this.setState({ currentUserPathway: updatedPathway });
        });
    };

    render() {
        const {
            page,
            limit = 5,
            currentUserPathway,
            pathways,
            currentPathway,
            hideLoadMore,
            animationClass,
            calendarEventInfo,
            calendarModal,
            teamUserPathways,
            completedPathwaysToDisplay,
            completedPathways = [],
            showCompletedPathways,
            pathwayViewLoad,
            individualUserPathways
        } = this.state;

        const {
            currentWidth,
            collectionsById,
            userCollectionsById,
            userCompany,
            isVerbAdmin,
            history,
            teamsById = {},
            pathwayView = ''
        } = this.props;

        const components = {
            browse: BrowsePathways,
            detail: PathwayDetails,
            myPathway: MyPathway,
            loading: LoadingIndicator
        };

        const pathwayOptions = [...individualUserPathways, ...teamUserPathways].sort(
            (a, b) => (a.name.toLowerCase() > b.name.toLowerCase()) - (a.name.toLowerCase() < b.name.toLowerCase())
        );

        const componentName = pathwayViewLoad || !pathwayView ? 'loading' : pathwayView;

        const ActiveComponent = components[componentName];

        return currentWidth > 450 || pathwayViewLoad ? (
            <>
                <div
                    className={cns(
                        `pathway-panel-container col-12 ${
                            pathwayViewLoad || !pathwayView ? 'pathway-panel-loading-view' : ''
                        }`,
                        animationClass
                    )}
                >
                    <ActiveComponent
                        pathways={pathways}
                        userPathway={currentUserPathway}
                        pathway={currentPathway}
                        page={page}
                        limit={limit}
                        fetchPathways={this.fetchPathways}
                        openPathwayDetails={this.openPathwayDetails}
                        onClickBack={this.onClickBack}
                        collectionsById={collectionsById}
                        selectPathway={this.selectPathway}
                        userCollectionsById={userCollectionsById}
                        hideLoadMore={hideLoadMore}
                        loggedInUserCompany={userCompany}
                        isVerbAdmin={isVerbAdmin}
                        history={history}
                        toggleCalendarModal={this.toggleCalendarModal}
                        pathwayOptions={[...pathwayOptions, ...completedPathwaysToDisplay]}
                        teamsById={teamsById}
                        handleShowCompletedPathways={this._handleShowCompletedPathways}
                        showCompletedPathways={showCompletedPathways}
                        completedPathways={completedPathways}
                        updateUserPathway={this.updateUserPathway}
                        setCurrentUserPathway={this.setCurrentUserPathway}
                        updateUserPathwayContentItem={this.updateUserPathwayContentItem}
                    />
                </div>
                <ScheduleModal
                    closeModal={this.toggleCalendarModal}
                    modalActiveSlide={2}
                    eventInfo={calendarEventInfo}
                    isModalOpen={calendarModal}
                />
            </>
        ) : (
            <>
                <div className={`${pathwayViewLoad || !pathwayView ? 'pathway-panel-loading-view' : ''}`}>
                    <ActiveComponent
                        pathways={pathways}
                        userPathway={currentUserPathway}
                        pathway={currentPathway}
                        page={page}
                        limit={limit}
                        fetchPathways={this.fetchPathways}
                        openPathwayDetails={this.openPathwayDetails}
                        onClickBack={this.onClickBack}
                        collectionsById={collectionsById}
                        selectPathway={this.selectPathway}
                        userCollectionsById={userCollectionsById}
                        hideLoadMore={hideLoadMore}
                        isMobile={currentWidth < 450}
                        history={history}
                        toggleCalendarModal={this.toggleCalendarModal}
                        pathwayOptions={[...pathwayOptions, ...completedPathwaysToDisplay]}
                        teamsById={teamsById}
                        handleShowCompletedPathways={this._handleShowCompletedPathways}
                        showCompletedPathways={showCompletedPathways}
                        completedPathways={completedPathways}
                        updateUserPathway={this.updateUserPathway}
                        updateUserPathwayContentItem={this.updateUserPathwayContentItem}
                    />
                </div>
                <ScheduleModal
                    closeModal={this.toggleCalendarModal}
                    modalActiveSlide={2}
                    eventInfo={calendarEventInfo}
                    isModalOpen={calendarModal}
                />
            </>
        );
    }
}
